import INodeAgent from 'types/INodeAgent';

// The node's slot budget as the backend reports it: the budget, what it already
// accounts for (running workers' size, retiring workers' running tasks, queued
// starts), and what is left. Null when the agent has no budget.
export interface SlotBudget {
  budget: number;
  inUse: number;
  free: number;
}

export function slotBudgetOf(node?: Pick<INodeAgent, 'slot_budget' | 'slots_in_use'> | null): SlotBudget | null {
  if (!node || node.slot_budget == null) return null;
  const inUse = node.slots_in_use ?? 0;
  return { budget: node.slot_budget, inUse, free: Math.max(node.slot_budget - inUse, 0) };
}

// The growth a request adds: what it asks for beyond what it transfers — a
// replacement's group, a resize's current size. Mirrors the backend's rule that
// only growth past the budget is refused, so a shrink or a same-size replacement
// fits even on a node already over budget.
export function slotsNeeded(requested: number, transfer = 0): number {
  return Math.max(requested - transfer, 0);
}

export function fitsSlotBudget(view: SlotBudget | null, requested: number, transfer = 0): boolean {
  return !view || slotsNeeded(requested, transfer) <= view.free;
}

export function slotBudgetHint(view: SlotBudget | null, requested: number, transfer = 0): string | null {
  if (!view) return null;
  const needed = slotsNeeded(requested, transfer);
  if (needed <= view.free) return `${view.free} of ${view.budget} slots free on this node.`;
  return `Needs ${needed} more slot${needed === 1 ? '' : 's'}; ${view.free} of ${view.budget} free on this node.`;
}
